import React, { createContext, useContext, useEffect, useMemo, useState } from "react";


const CART_STORAGE_KEY = "yu_tech_shop_cart";
const CartContext = createContext(null);


function readStoredCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}


export function CartProvider({ children }) {
  const [items, setItems] = useState(readStoredCart);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const api = useMemo(() => {
    const addItem = (product, quantity = 1) => {
      setItems((current) => {
        const existing = current.find((item) => item.id === product.id);
        if (existing) {
          return current.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
          );
        }

        return [
          ...current,
          {
            id: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            sku: product.sku,
            primary_image: product.primary_image || null,
            quantity,
          },
        ];
      });
    };

    const updateQuantity = (productId, quantity) => {
      setItems((current) =>
        current
          .map((item) => (item.id === productId ? { ...item, quantity: Math.max(1, quantity) } : item))
          .filter((item) => item.quantity > 0)
      );
    };

    const removeItem = (productId) => {
      setItems((current) => current.filter((item) => item.id !== productId));
    };

    const clearCart = () => {
      setItems([]);
    };

    return { addItem, updateQuantity, removeItem, clearCart };
  }, []);

  const totalItems = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  return (
    <CartContext.Provider value={{ items, totalItems, ...api }}>
      {children}
    </CartContext.Provider>
  );
}


export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
}
